import { mapDatasetFiltersToPayload } from "@/helpers/datasetAttributeHelper";
import { DatasetFilter } from "@/types/dataset";
import {
  PostWidgetAnalysisConfigPayload,
  WidgetConfig,
  WidgetType
} from "@/types/widget";

export type WidgetGridBreakpoint = 768 | 1024 | 1366 | 1920 | 3840;

export type WidgetGridSection = {
  type: "bn" | "chart";
  widgets: WidgetConfig[];
};

// chart tiers: [1, 2, 3-4, 5-8, 9+] widgets; bn tiers: [1, 2, 3, 4+] widgets
const COL_RULES: Record<
  WidgetGridBreakpoint,
  { chart: number[]; bn: number[] }
> = {
  768: { chart: [1, 1, 2, 2, 2], bn: [2, 2, 3, 3] },
  1024: { chart: [1, 2, 2, 2, 2], bn: [2, 2, 3, 3] },
  1366: { chart: [1, 2, 2, 2, 2], bn: [3, 2, 3, 4] },
  1920: { chart: [1, 2, 2, 2, 2], bn: [3, 2, 3, 4] },
  3840: { chart: [1, 2, 3, 4, 4], bn: [4, 4, 4, 6] }
};

export const getWidgetGridBreakpoint = (
  width: number
): WidgetGridBreakpoint => {
  if (width >= 3840) return 3840;
  if (width >= 1920) return 1920;
  if (width >= 1366) return 1366;
  if (width >= 1024) return 1024;
  return 768;
};

// Consecutive BN widgets share one grid row; chart widgets do the same.
// A chart between BN groups splits them into separate sections so each
// section's column count is driven only by its own widget count.
export const groupWidgetsBySection = (
  widgets: WidgetConfig[]
): WidgetGridSection[] => {
  const sections: WidgetGridSection[] = [];
  for (const widget of widgets) {
    const type = widget.chart_type === WidgetType.BIG_NUMBER ? "bn" : "chart";
    const last = sections.at(-1);
    if (last?.type === type) last.widgets.push(widget);
    else sections.push({ type, widgets: [widget] });
  }
  return sections;
};

const getSectionCols = (
  section: WidgetGridSection,
  bp: WidgetGridBreakpoint
): number => {
  const count = section.widgets.length;
  const rules = COL_RULES[bp];
  if (section.type === "bn") {
    if (count === 1) return rules.bn[0];
    if (count === 2) return rules.bn[1];
    if (count === 3) return rules.bn[2];
    return rules.bn[3];
  }
  if (count === 1) return rules.chart[0];
  if (count === 2) return rules.chart[1];
  if (count <= 4) return rules.chart[2];
  if (count <= 8) return rules.chart[3];
  return rules.chart[4];
};

// BN sections fill the last row with empty divs so a single BN never spans full
// width. Chart sections rely on CSS grid's natural placement (no empties).
export const getWidgetSectionLayout = (
  section: WidgetGridSection,
  bp: WidgetGridBreakpoint
) => {
  const cols = getSectionCols(section, bp);
  const remainder = section.widgets.length % cols;
  const emptySlots =
    section.type === "bn" && remainder > 0 ? cols - remainder : 0;
  return { cols, emptySlots };
};

export const mapPostWidgetAnalysisConfigPayload = (
  data: WidgetConfig,
  filters: DatasetFilter[]
): PostWidgetAnalysisConfigPayload => {
  const mappedFilter = mapDatasetFiltersToPayload(filters);

  return {
    id: data.dataset_id,
    dimension: data.dimension,
    measure: data.measure,
    aggregation: data.aggregation,
    order_by: data.configuration.order_by,
    ...(filters && { filters: mappedFilter }),
    ...(data.h3Configuration && {
      grid_config: {
        level: data.h3Configuration.resolution,
        aggregation_type: data.h3Configuration.aggregationType
      }
    }),
    ...(data.group_by && { group_by: data.group_by })
  };
};
